import React, { useState } from "react";
import { motion } from "framer-motion";
import { Copy, Check, ExternalLink, FileText, Sparkles, ArrowRight, ChevronDown, ChevronUp } from "lucide-react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import SEOHead from "@/components/SEOHead";

const ease = [0.16, 1, 0.3, 1];
const fadeUp = (delay = 0) => ({
  initial: { opacity: 0, y: 20 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-50px" },
  transition: { duration: 0.6, ease, delay },
});

const MASTER_PROMPT = `YOU ARE: A senior resume strategist, ATS expert, and professional editor with 15+ years of experience.
You will conduct a complete, end-to-end resume review and rewrite.

════════════════════════════════════
CONTEXT — FILL IN BEFORE USING
════════════════════════════════════
TARGET ROLE: [Job Title]
TARGET INDUSTRY: [Industry / Domain]
EXPERIENCE LEVEL: [Entry / Mid / Senior / Executive]
YEARS OF EXPERIENCE: [X years]
JOB DESCRIPTION: [Paste full JD here]
MY CURRENT RESUME: [Paste full resume here]

════════════════════════════════════
PHASE 1 — DIAGNOSTIC AUDIT
════════════════════════════════════

STEP 1 — FIRST IMPRESSION SCAN (7-second test)
Simulate a hiring manager's 7-second scan. Tell me:
— What is immediately clear (title, level, domain)?
— What is unclear or missing at first glance?
— Does the overall structure pass the visual scan test?
— What is the single biggest red flag on this resume right now?

STEP 2 — ATS KEYWORD GAP
Compare my resume against the job description. Output:
— Hard-skill keywords in the JD completely missing from my resume
— Keywords present but buried — note where to surface them
— Soft-skill language from the JD I should mirror (exact phrases)
— Estimated ATS match score (0–100%) with 1-sentence reasoning
— Do not suggest adding anything I don't actually have

STEP 3 — LANGUAGE AND TONE AUDIT
Review the full resume for language quality. Flag every instance of:
— Weak opening verbs ("helped," "assisted," "worked on," "responsible for," "involved in")
— Passive voice ("was responsible for," "were managed by")
— Vague filler words ("various," "multiple," "a number of," "several")
— Cliché phrases ("results-driven," "passionate," "team player," "hardworking," "dynamic")
— Tense errors (current role = present tense; all past roles = past tense)
— Pronoun use ("I," "my," "we" — all must be removed)
— Bullets over 20 words (flag for trimming)
— Duplicate opening verbs across bullets
List each issue with its location: [Section → Bullet or sentence]

════════════════════════════════════
PHASE 2 — SECTION REWRITES
════════════════════════════════════

STEP 4 — PROFESSIONAL SUMMARY REWRITE
Rewrite my professional summary using this exact 3-sentence structure:
Sentence 1 — Who I am: [Title] with [X years] of experience in [domain/specialty]
Sentence 2 — What I deliver: core skill + one quantified proof point
Sentence 3 — My value to this role: forward-looking, specific to the target position
Rules: 3 sentences max, under 60 words total, one number minimum, no clichés, no pronouns, no passive voice, naturally include 2–3 high-priority JD keywords

STEP 5 — EXPERIENCE BULLETS FULL REWRITE
Rewrite every bullet in my work experience section using:

STRUCTURE (CAR formula):
[Strong Action Verb] + [What I did / scope] + [Tool/method used] + [Measurable result]

ACTION VERBS — Replace any weak verb. Assign from the correct category:
  Leadership → Spearheaded, Championed, Directed, Orchestrated, Mobilized
  Delivery   → Launched, Deployed, Executed, Shipped, Implemented
  Growth     → Grew, Scaled, Expanded, Accelerated, Generated
  Improvement→ Streamlined, Optimized, Redesigned, Automated, Reduced
  Analysis   → Synthesized, Evaluated, Diagnosed, Forecasted, Identified
  Collaboration → Partnered, Aligned, Facilitated, Coordinated, Unified

QUANTIFICATION — Every bullet must contain at least one of:
  % improvement / $ value / time saved / volume handled / team size / error rate / frequency

LENGTH — Max 20 words per bullet. Cut anything that doesn't add impact.

FORMATTING RULES:
  — Past tense for all previous roles, present tense for current role
  — No pronouns
  — No periods at end of bullets (or add them consistently — pick one style)
  — No two bullets start with the same verb

For each bullet output: Original → Rewritten → Metric type added (or "flag: metric needed — [what to find]")

STEP 6 — SKILLS SECTION RESTRUCTURE
— Create max 4 category headers relevant to the target role
— Order skills within each category by JD relevance (highest first)
— Remove skills that are outdated, redundant, or too generic for my level
— Add skills clearly demonstrated in my bullets but missing from the skills list
— Flag any JD-required skill I don't have listed

STEP 7 — EDUCATION AND CERTIFICATIONS
— Suggest what to emphasize vs. condense based on my experience level
— Flag any certification that is expired, irrelevant, or should be removed
— Recommend placement (before or after experience) based on my seniority
— Suggest if any JD-relevant certifications are missing and worth pursuing

════════════════════════════════════
PHASE 3 — FINAL POLISH
════════════════════════════════════

STEP 8 — CONSISTENCY CHECK
— Tense: current role present tense, all past roles past tense — no exceptions
— Punctuation: bullet periods either always present or always absent — enforce one standard
— Number formatting: numerals for 10+ and all metrics; spell out one through nine in prose
— Capitalization: job titles, tools, and company names uniform throughout
— Verb variety: no action verb used more than twice — flag repeats and suggest replacements
— Date formatting: consistent [Month Year – Month Year] throughout

STEP 9 — FINAL DELIVERABLE
Output the complete, fully rewritten resume in this order:
1. Resume headline (under 12 words: [Title] | [Specialization] | [Key differentiator])
2. Professional summary (3 sentences)
3. Work experience (all roles, all bullets rewritten)
4. Skills (restructured)
5. Education and certifications (optimized)

Then provide a final summary table:
  — ATS match score: before vs. after
  — Number of weak verbs fixed
  — Number of bullets quantified
  — Top 3 improvements made
  — 1 thing I still need to verify or add myself

IMPORTANT: Do not invent experience, metrics, or skills. Where a metric is missing, insert a placeholder: [ADD: X metric — check performance review / dashboard / manager]`;

const CopyButton = ({ text, label = "Copy Prompt" }) => {
  const [copied, setCopied] = useState(false);
  const handleCopy = () => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };
  return (
    <button onClick={handleCopy} data-testid="copy-master-prompt"
      className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-xs font-medium transition-all duration-300 ${copied ? "bg-green-500 text-white" : "bg-brand text-white hover:bg-brand-hover"}`}>
      {copied ? <><Check size={13} /> Copied!</> : <><Copy size={13} /> {label}</>}
    </button>
  );
};

const STEPS = [
  { num: "01", icon: FileText, title: "Get the Template", desc: "Start with a clean, ATS-friendly resume template from our recommended guide. Build your base before optimizing." },
  { num: "02", icon: Copy, title: "Copy the Master Prompt", desc: "Copy the AI prompt below. Fill in your target role, job description, and paste your current resume into it." },
  { num: "03", icon: Sparkles, title: "Paste into ChatGPT or Claude", desc: "Open ChatGPT (GPT-4o) or Claude and paste the full prompt. Let the AI do a complete 9-step review and rewrite." },
];

export default function ResumeOptimizerPage() {
  const [expanded, setExpanded] = useState(false);

  return (
    <div data-testid="resume-optimizer-page" className="bg-white">
      <SEOHead
        title="Free AI Resume Optimizer | ApplyStrat"
        description="Use this expert-crafted AI prompt to get a complete resume review, ATS optimization, and full rewrite — free. Paste into ChatGPT or Claude."
        canonical="https://applystrat.com/resume-optimizer"
      />

      {/* ── HERO ── */}
      <section className="pt-36 pb-16 text-center" style={{ background: "radial-gradient(circle at 50% -20%, rgba(255,82,0,0.10) 0%, transparent 60%)" }}>
        <div className="max-w-3xl mx-auto px-6">
          <motion.div {...fadeUp()}>
            <span className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-brand-light text-brand text-xs font-medium uppercase tracking-widest border border-brand/20 mb-6">
              <span className="w-1.5 h-1.5 rounded-full bg-brand animate-pulse" />
              Free Tool
            </span>
            <h1 className="text-5xl md:text-6xl font-medium tracking-tighter text-ink mb-5 leading-tight">
              Optimize Your Resume<br /><span className="text-brand">with AI — Free</span>
            </h1>
            <p className="text-lg text-ink-secondary max-w-xl mx-auto leading-relaxed">
              A 9-step AI framework built by resume strategists. Get an ATS audit, full language rewrite, and polished final resume — just paste and go.
            </p>
          </motion.div>
        </div>
      </section>

      {/* ── 3 STEPS ── */}
      <section className="py-20 bg-surface-raised" data-testid="optimizer-steps">
        <div className="max-w-5xl mx-auto px-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {STEPS.map((s, i) => (
              <motion.div key={s.num} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.5, ease, delay: i * 0.1 }}
                className="bg-white rounded-3xl p-8 shadow-sm border border-black/5">
                <span className="text-4xl font-medium text-brand/20 block mb-4">{s.num}</span>
                <h3 className="text-ink font-medium text-base mb-2">{s.title}</h3>
                <p className="text-ink-secondary text-sm leading-relaxed">{s.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ── TEMPLATE LINK ── */}
      <section className="py-16 bg-white">
        <div className="max-w-3xl mx-auto px-6 text-center">
          <motion.div {...fadeUp()}>
            <h2 className="text-2xl font-medium text-ink mb-4">Step 1 — Start with a Clean Template</h2>
            <p className="text-ink-secondary text-sm mb-6 leading-relaxed">Before optimizing, make sure your resume is structured correctly. Use this beginner-friendly guide to build a strong base.</p>
            <a href="https://www.coursera.org/articles/generative-ai-resume" target="_blank" rel="noopener noreferrer" data-testid="template-link"
              className="inline-flex items-center gap-2 px-6 py-3 rounded-full border-2 border-brand text-brand font-medium text-sm hover:bg-brand-light transition-all duration-200">
              View Resume Template Guide <ExternalLink size={14} />
            </a>
          </motion.div>
        </div>
      </section>

      {/* ── MASTER PROMPT ── */}
      <section className="py-16 bg-surface-raised" data-testid="master-prompt-section">
        <div className="max-w-4xl mx-auto px-6">
          <motion.div {...fadeUp()} className="text-center mb-10">
            <h2 className="text-3xl md:text-4xl font-medium text-ink mb-4">Step 2 — Copy the Master Prompt</h2>
            <p className="text-ink-secondary text-sm max-w-xl mx-auto leading-relaxed">
              Fill in the context fields at the top (role, JD, your resume), then paste the entire prompt into <strong>ChatGPT-4o</strong> or <strong>Claude Sonnet</strong>.
            </p>
          </motion.div>

          <motion.div {...fadeUp(0.1)} className="bg-ink rounded-3xl overflow-hidden shadow-xl">
            {/* Prompt header bar */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-white/10">
              <div className="flex items-center gap-3">
                <div className="flex gap-1.5">
                  <span className="w-3 h-3 rounded-full bg-red-400/60" />
                  <span className="w-3 h-3 rounded-full bg-yellow-400/60" />
                  <span className="w-3 h-3 rounded-full bg-green-400/60" />
                </div>
                <span className="text-white/40 text-xs font-mono">master-resume-prompt.txt</span>
              </div>
              <CopyButton text={MASTER_PROMPT} label="Copy Full Prompt" />
            </div>

            {/* Prompt preview */}
            <div className="relative">
              <pre className={`text-green-300/80 text-xs font-mono leading-relaxed p-6 overflow-x-auto whitespace-pre-wrap transition-all duration-500 ${expanded ? "" : "max-h-80 overflow-hidden"}`}>
                {MASTER_PROMPT}
              </pre>
              {!expanded && (
                <div className="absolute bottom-0 left-0 right-0 h-24 bg-gradient-to-t from-[#1D1D1F] to-transparent" />
              )}
            </div>

            <div className="flex justify-center pb-5">
              <button onClick={() => setExpanded(!expanded)}
                className="inline-flex items-center gap-1.5 text-white/50 hover:text-white text-xs font-medium transition-colors">
                {expanded ? <><ChevronUp size={14} /> Show less</> : <><ChevronDown size={14} /> Show full prompt</>}
              </button>
            </div>
          </motion.div>

          <motion.p {...fadeUp(0.2)} className="text-center text-ink-secondary text-xs mt-5">
            Works best with GPT-4o (ChatGPT) or Claude Sonnet 3.5+. Free tier works for most resumes.
          </motion.p>
        </div>
      </section>

      {/* ── BEFORE / AFTER EXAMPLES ── */}
      <section className="py-20 bg-white" data-testid="before-after-section">
        <div className="max-w-4xl mx-auto px-6">
          <motion.div {...fadeUp()} className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-medium text-ink mb-3">Before & After</h2>
            <p className="text-ink-secondary text-sm max-w-lg mx-auto">See what the AI transformation actually looks like on real resume content.</p>
          </motion.div>

          <div className="space-y-8">
            {/* Example 1 — Bullet */}
            <motion.div {...fadeUp(0.1)} className="rounded-3xl overflow-hidden border border-black/5 shadow-sm">
              <div className="bg-surface-raised px-6 py-3 flex items-center justify-between">
                <span className="text-ink text-xs font-medium uppercase tracking-wider">Example 1 — Experience Bullet</span>
                <span className="px-3 py-1 rounded-full bg-brand-light text-brand text-xs font-medium">Backend SDE · 4 years</span>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 divide-y md:divide-y-0 md:divide-x divide-black/5">
                <div className="p-7">
                  <p className="text-red-400 font-medium text-xs uppercase tracking-wider mb-3">Before</p>
                  <p className="text-ink-secondary text-sm leading-relaxed italic">
                    "Responsible for helping the team with backend development and deployment tasks across multiple projects."
                  </p>
                </div>
                <div className="p-7 bg-[#FAFAFA]">
                  <p className="text-green-500 font-medium text-xs uppercase tracking-wider mb-3">After</p>
                  <p className="text-ink text-sm leading-relaxed">
                    "Reduced deployment time 40% by redesigning CI/CD pipeline using GitHub Actions, cutting 6-hour weekly release cycles to under 90 minutes."
                  </p>
                </div>
              </div>
            </motion.div>

            {/* Example 2 — Summary */}
            <motion.div {...fadeUp(0.15)} className="rounded-3xl overflow-hidden border border-black/5 shadow-sm">
              <div className="bg-surface-raised px-6 py-3 flex items-center justify-between">
                <span className="text-ink text-xs font-medium uppercase tracking-wider">Example 2 — Professional Summary</span>
                <span className="px-3 py-1 rounded-full bg-brand-light text-brand text-xs font-medium">ML Engineer · 3 years</span>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 divide-y md:divide-y-0 md:divide-x divide-black/5">
                <div className="p-7">
                  <p className="text-red-400 font-medium text-xs uppercase tracking-wider mb-3">Before</p>
                  <p className="text-ink-secondary text-sm leading-relaxed italic">
                    "Passionate machine learning engineer with 3 years of experience. Good at Python and deep learning. Hardworking team player looking for new opportunities."
                  </p>
                </div>
                <div className="p-7 bg-[#FAFAFA]">
                  <p className="text-green-500 font-medium text-xs uppercase tracking-wider mb-3">After</p>
                  <p className="text-ink text-sm leading-relaxed">
                    "ML engineer with 3 years deploying production NLP and computer vision systems. Reduced model inference latency 55% at [Company] through quantization and ONNX optimization. Targeting senior ML roles at product-led AI companies."
                  </p>
                </div>
              </div>
            </motion.div>

            {/* Example 3 — Skills */}
            <motion.div {...fadeUp(0.2)} className="rounded-3xl overflow-hidden border border-black/5 shadow-sm">
              <div className="bg-surface-raised px-6 py-3 flex items-center justify-between">
                <span className="text-ink text-xs font-medium uppercase tracking-wider">Example 3 — Skills Section</span>
                <span className="px-3 py-1 rounded-full bg-brand-light text-brand text-xs font-medium">Full-stack SDE</span>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 divide-y md:divide-y-0 md:divide-x divide-black/5">
                <div className="p-7">
                  <p className="text-red-400 font-medium text-xs uppercase tracking-wider mb-3">Before</p>
                  <p className="text-ink-secondary text-sm leading-relaxed italic font-mono">
                    Python, Java, Git, HTML, CSS, MySQL, Communication, Problem Solving, Team Work, Microsoft Office
                  </p>
                </div>
                <div className="p-7 bg-[#FAFAFA]">
                  <p className="text-green-500 font-medium text-xs uppercase tracking-wider mb-3">After</p>
                  <div className="space-y-2 text-sm">
                    <p><span className="text-ink font-medium">Languages:</span> <span className="text-ink-secondary">Python, Java, TypeScript, SQL</span></p>
                    <p><span className="text-ink font-medium">Frameworks:</span> <span className="text-ink-secondary">React, FastAPI, Spring Boot, Node.js</span></p>
                    <p><span className="text-ink font-medium">Cloud / Infra:</span> <span className="text-ink-secondary">AWS (EC2, S3, Lambda), Docker, GitHub Actions</span></p>
                    <p><span className="text-ink font-medium">Databases:</span> <span className="text-ink-secondary">PostgreSQL, MongoDB, Redis</span></p>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ── QUICK PROMPTS ── */}
      <section className="py-16 bg-white">
        <div className="max-w-3xl mx-auto px-6 text-center">
          <motion.div {...fadeUp()}>
            <h2 className="text-2xl font-medium text-ink mb-3">Want Shorter, Targeted Prompts?</h2>
            <p className="text-ink-secondary text-sm mb-8 leading-relaxed">We've also published 10 specific prompts for fixing individual sections — summaries, bullet points, ATS optimization, and more.</p>
            <Button asChild size="default" className="shadow-sm">
              <Link to="/blog/10-prompts-fix-resume" data-testid="link-to-blog-post">
                Read: 10 Prompts I Used to Fix My Resume <ArrowRight size={15} />
              </Link>
            </Button>
          </motion.div>
        </div>
      </section>

      {/* ── CTA ── */}
      <section className="py-20 bg-ink" data-testid="optimizer-cta">
        <div className="max-w-3xl mx-auto px-6 text-center">
          <motion.div {...fadeUp()}>
            <h2 className="text-3xl md:text-4xl font-medium text-white mb-4">Want Hands-On Help?</h2>
            <p className="text-white/60 text-base mb-8 leading-relaxed">
              The AI prompt is a great start. If you want a real strategist to review your profile, target the right roles, and handle applications for you — that's what ApplyStrat does.
            </p>
            <Button asChild size="lg" className="shadow-lg hover:scale-[1.02]">
              <Link to="/apply" data-testid="optimizer-apply-cta">
                Apply to Work With Us <ArrowRight size={18} />
              </Link>
            </Button>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
